// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ArrowUp, Square, X } from "lucide-react";
import { type KeyboardEvent, type RefObject, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MAX_QUESTION_CHARS, type PageContext } from "@/features/assistant/types";

export function Composer({
  inputRef,
  busy,
  hasMessages,
  context,
  onSend,
  onStop,
  onDismissContext,
}: {
  inputRef: RefObject<HTMLTextAreaElement | null>;
  busy: boolean;
  hasMessages: boolean;
  context: PageContext | null;
  /** Returns whether the question was taken; the input is cleared only then. */
  onSend: (question: string) => boolean;
  onStop: () => void;
  onDismissContext: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const canSend = value.trim().length > 0 && !busy;

  function send() {
    if (!canSend) return;
    if (onSend(value)) setValue("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    send();
  }

  return (
    <div className="border-t px-3 pt-2.5 pb-3">
      <div className="rounded-xl border px-2.5 py-2 focus-within:border-ring">
        <Textarea
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          maxLength={MAX_QUESTION_CHARS}
          rows={1}
          placeholder={t(hasMessages ? "assistant.composer.placeholderFollowUp" : "assistant.composer.placeholder")}
          className="max-h-40 min-h-9 resize-none border-0 p-0 shadow-none focus-visible:ring-0 dark:bg-transparent"
        />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          {context ? (
            <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
              <span className="text-muted-foreground">{t("assistant.composer.onPage")}</span>
              <span className="truncate font-medium">{context.label}</span>
              <button
                type="button"
                aria-label={t("assistant.composer.removeContext")}
                onClick={onDismissContext}
                className="rounded-full p-0.5 text-muted-foreground hover:bg-background"
              >
                <X className="size-3" />
              </button>
            </span>
          ) : (
            <span />
          )}
          {busy ? (
            <Button
              size="icon"
              variant="outline"
              className="size-8"
              aria-label={t("assistant.composer.stop")}
              onClick={onStop}
            >
              <Square className="size-3.5 fill-current" />
            </Button>
          ) : (
            <Button
              size="icon"
              className="size-8"
              aria-label={t("assistant.composer.send")}
              disabled={!canSend}
              onClick={send}
            >
              <ArrowUp className="size-4" />
            </Button>
          )}
        </div>
      </div>
      <p className="mt-1.5 text-center text-[11px] text-muted-foreground">{t("assistant.composer.hint")}</p>
    </div>
  );
}
